import { Tabs, Tab, Box } from '@mui/material';
import {
  HOME_TAB_URL,
  MainTab,
  useTabNavigation,
} from '../../store/mainTabsStore';
import HomeIcon from '../../assets/icons/HomeIcon';
import PenIcon from '../../assets/icons/PenIcon';
import CloseIcon from '../../assets/icons/CloseIcon';
import {
  MainTabsWrapStyles,
  TabContentStyles,
  TabStyles,
  SvgWrapStyles,
  MainTabsStyles,
} from './MainTabsProps';
import { TabName } from './TabName/TabName';
import { useEffect, useRef, useState } from 'react';
import { TestIds } from '../../utils/dataTestIds';
import { useClient } from '../../store/clientStore';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import ContextMenu from './ContextMenu/ContextMenu';
import { SCROLL_BUTTON } from '../../utils/consts';

const MainTabs = () => {
  const { tabs, activeTab, makeTabActive, removeTab } = useTabNavigation();
  const { removeSubTab } = useClient();
  const [confirmCloseDialog, setConfirmCloseDialog] = useState<MainTab | null>(
    null
  );
  const tabClosedResolverRef = useRef<((value: boolean) => void) | null>(null);
  const [contextMenuData, setContextMenuData] = useState<{
    clientX: number;
    clientY: number;
    tabClicked: MainTab;
  } | null>(null);

  function closeTab(tab: MainTab) {
    removeTab(tab.url);
    if (tab.clientFormData) {
      removeSubTab(tab.clientFormData.name);
    }
  }

  function reorderTabs(tabs: MainTab[], tabClicked: MainTab) {
    const tabIndex = tabs.findIndex((t) => t.url === tabClicked.url);
    const reoderedTabs = [
      tabs[tabIndex],
      ...tabs.slice(0, tabIndex).reverse(),
      ...tabs.slice(tabIndex + 1),
    ];
    return reoderedTabs;
  }

  const handleCloseContextMenu = () => {
    setContextMenuData(null);
  };

  const handleCloseClick = (tab: MainTab) => {
    if (tab.isDirty) {
      setConfirmCloseDialog(tab);
      return;
    }

    closeTab(tab);
  };

  const handleCloseOthersClick = async (tabClicked: MainTab) => {
    const reoderedTabs = reorderTabs(tabs, tabClicked);
    for (const t of reoderedTabs) {
      if (t.url === tabClicked.url) continue;

      if (t.isDirty) {
        const promise = new Promise((resolve) => {
          tabClosedResolverRef.current = resolve;
        });
        makeTabActive(t.url);
        setConfirmCloseDialog(t);
        await promise;
      } else {
        closeTab(t);
      }
    }
  };

  const handleCloseSavedClick = (tabClicked: MainTab) => {
    const indexOfTabClicked = tabs.findIndex((t) => t.url === tabClicked.url);
    const saved = tabs.filter((t) => !t.isDirty);
    saved.forEach((t) => closeTab(t));

    let nextActiveTab;
    if (saved.find((t) => t.url === tabClicked.url)) {
      for (let i = indexOfTabClicked - 1; i >= 0; i--) {
        const tab = tabs[i];
        if (tab.isDirty) {
          nextActiveTab = tab.url;
          break;
        }
      }
    }
    nextActiveTab && makeTabActive(nextActiveTab);
  };

  const handleCloseAllClick = async (tabClicked: MainTab) => {
    const reoderedTabs = reorderTabs(tabs, tabClicked);
    let nextActiveTab;
    for (const t of reoderedTabs) {
      if (t.isDirty) {
        const tabClosedPromise = new Promise<boolean>((resolve) => {
          tabClosedResolverRef.current = resolve;
        });
        makeTabActive(t.url);
        setConfirmCloseDialog(t);
        const tabClosed = await tabClosedPromise;
        if (!tabClosed && !nextActiveTab) {
          nextActiveTab = t.url;
          continue;
        }
      } else {
        closeTab(t);
      }
    }
    makeTabActive(nextActiveTab || HOME_TAB_URL);
  };

  const handleMiddleMouseClick = (
    event: React.MouseEvent<HTMLElement>,
    tab: MainTab
  ) => {
    if (event.button !== SCROLL_BUTTON) return;

    event.preventDefault();
    event.stopPropagation();

    if (tab.isDirty) {
      setConfirmCloseDialog(tab);
      return;
    }

    closeTab(tab);
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const t = setTimeout(() => {
      const btns = document.querySelectorAll('.MuiTabs-scrollButtons');
      const btn = (btns && btns[1]) as HTMLElement | undefined;
      if (btn) btn.click();
    }, 100);

    return () => clearTimeout(t);
  }, [tabs.length]);

  return (
    <Box
      sx={MainTabsWrapStyles}
      className="main-tabs"
      data-testid={TestIds.MainTabs.Container}
    >
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => {
          makeTabActive(newValue);
        }}
        variant="scrollable"
        scrollButtons="auto"
        TabIndicatorProps={{
          style: {
            backgroundColor: '#fff',
          },
        }}
        sx={MainTabsStyles}
      >
        <Tab
          onContextMenu={(e) => e.preventDefault()}
          key={HOME_TAB_URL}
          value={HOME_TAB_URL}
          className="main-tab main-tab--home"
          sx={TabStyles({
            isActive: activeTab === HOME_TAB_URL,
            isNextActive: activeTab === tabs[0]?.url,
          })}
          icon={<HomeIcon />}
          data-testid={TestIds.MainTabs.HomeTab}
          label={<Box sx={TabContentStyles} />}
          iconPosition="start"
        />
        {tabs.map((tab, index, filteredTabs) => {
          const isActive = activeTab === tab.url;
          const isNextActive = activeTab === filteredTabs[index + 1]?.url;
          return (
            <Tab
              onMouseDown={(event) => handleMiddleMouseClick(event, tab)}
              onContextMenu={(e) => {
                e.preventDefault();
                setContextMenuData({
                  clientX: e.clientX,
                  clientY: e.clientY,
                  tabClicked: tab,
                });
              }}
              key={tab.url}
              value={tab.url}
              className="main-tab"
              sx={TabStyles({ isActive, isNextActive })}
              icon={
                tab.isDirty ? (
                  <PenIcon data-testid={TestIds.MainTabs.PenIcon} />
                ) : undefined
              }
              label={
                <Box sx={TabContentStyles}>
                  <TabName name={tab.name} />
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      if (tab.isDirty) {
                        setConfirmCloseDialog(tab);
                        return;
                      }
                      closeTab(tab);
                    }}
                    sx={SvgWrapStyles}
                  >
                    <CloseIcon data-testid={TestIds.MainTabs.CloseIcon} />
                  </Box>
                </Box>
              }
              iconPosition="start"
            />
          );
        })}
      </Tabs>
      <ConfirmationDialog
        state="error"
        open={Boolean(confirmCloseDialog)}
        title={
          <>
            <span>
              Tab {confirmCloseDialog?.name || ''} has unsaved changes.
            </span>
            <br />
            <span>Do you want to close without saving?</span>
            <br />
            <span>Any unsaved changes will be lost.</span>
          </>
        }
        cancelButton={{
          text: 'Cancel',
          onClick: () => {
            setConfirmCloseDialog(null);
            tabClosedResolverRef.current?.(false);
          },
        }}
        confirmButton={{
          text: 'Close without saving',
          onClick: () => {
            setConfirmCloseDialog(null);
            closeTab(confirmCloseDialog!);
            tabClosedResolverRef.current?.(true);
          },
          skipCloseCallback: true,
        }}
        onClose={() => {
          setConfirmCloseDialog(null);
          tabClosedResolverRef.current?.(false);
        }}
      />
      {contextMenuData && (
        <ContextMenu
          open={Boolean(contextMenuData)}
          clientX={contextMenuData.clientX || 0}
          clientY={contextMenuData.clientY || 0}
          onCloseContextMenu={handleCloseContextMenu}
          onCloseClick={handleCloseClick}
          onCloseOthersClick={handleCloseOthersClick}
          onCloseSavedClick={handleCloseSavedClick}
          onCloseAllClick={handleCloseAllClick}
          tabClicked={contextMenuData.tabClicked}
        />
      )}
    </Box>
  );
};

export default MainTabs;
